const DEFAULT_ENTITY_LABELS = {
    singular: 'seat',
    plural: 'seats',
    arrangement: 'Seating Arrangement',
    row: 'Row',
    section: 'Section',
    showRowInEntityLabels: true,
    accessible: 'Wheelchair Seat',
};

function normalizeLabel(value, fallback) {
    const normalizedValue = typeof value === 'string' ? value.trim() : '';

    return normalizedValue || fallback;
}

function capitalizeFirst(value) {
    const normalizedValue = normalizeLabel(value, '');

    if (normalizedValue === '') {
        return '';
    }

    return normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1);
}

function getIndefiniteArticle(value) {
    return /^[aeiou]/i.test(normalizeLabel(value, '')) ? 'an' : 'a';
}

function getEntityLabelsFromDataset(dataset = {}) {
    return {
        singular: normalizeLabel(dataset.entityLabelSingular, DEFAULT_ENTITY_LABELS.singular),
        plural: normalizeLabel(dataset.entityLabelPlural, DEFAULT_ENTITY_LABELS.plural),
        arrangement: normalizeLabel(dataset.entityArrangementLabel, DEFAULT_ENTITY_LABELS.arrangement),
        row: normalizeLabel(dataset.entityRowLabel, DEFAULT_ENTITY_LABELS.row),
        section: normalizeLabel(dataset.entitySectionLabel, DEFAULT_ENTITY_LABELS.section),
        showRowInEntityLabels: dataset.showRowInEntityLabels !== 'false',
        accessible: normalizeLabel(dataset.accessibleEntityLabel, DEFAULT_ENTITY_LABELS.accessible),
    };
}

function getEntityLabelsForSelectedSeats(selectedSeats, mapStates, activeMapState = null) {
    const selectedMapIds = Array.from(new Set(
        selectedSeats
            .map(seat => seat.seat_map_id)
            .filter(seatMapId => seatMapId)
    ));

    if (selectedMapIds.length === 0) {
        return activeMapState && activeMapState.entityLabels
            ? activeMapState.entityLabels
            : DEFAULT_ENTITY_LABELS;
    }

    const selectedMapLabels = selectedMapIds
        .map(seatMapId => mapStates.find(mapState => mapState.id === seatMapId))
        .filter(mapState => mapState && mapState.entityLabels)
        .map(mapState => mapState.entityLabels);

    if (selectedMapLabels.length === 0) {
        return DEFAULT_ENTITY_LABELS;
    }

    const pluralLabels = new Set(selectedMapLabels.map(labels => labels.plural));

    if (pluralLabels.size > 1) {
        return {
            ...DEFAULT_ENTITY_LABELS,
            singular: 'item',
            plural: 'items',
            accessible: DEFAULT_ENTITY_LABELS.accessible,
        };
    }

    return selectedMapLabels[0];
}

function areDefaultSeatEntityLabels(labels = DEFAULT_ENTITY_LABELS) {
    return normalizeLabel(labels.singular, DEFAULT_ENTITY_LABELS.singular).toLowerCase() === DEFAULT_ENTITY_LABELS.singular &&
        normalizeLabel(labels.plural, DEFAULT_ENTITY_LABELS.plural).toLowerCase() === DEFAULT_ENTITY_LABELS.plural;
}

function getChooseEntityCopy(labels = DEFAULT_ENTITY_LABELS) {
    return `CHOOSE YOUR ${capitalizeFirst(normalizeLabel(labels.plural, DEFAULT_ENTITY_LABELS.plural)).toUpperCase()}`;
}

function getReservedTicketTypeCardCopy(labels = DEFAULT_ENTITY_LABELS) {
    if (areDefaultSeatEntityLabels(labels)) {
        return {
            title: 'RESERVED TICKETS',
            description: 'Pick your seats',
        };
    }

    return {
        title: 'RESERVED ITEMS',
        description: `Pick your ${normalizeLabel(labels.plural, DEFAULT_ENTITY_LABELS.plural)}`,
    };
}

function getSelectedSeatsIndicatorText(selectedSeats, mapStates, activeMapState = null) {
    const seatsCount = selectedSeats.length;
    const labels = getEntityLabelsForSelectedSeats(selectedSeats, mapStates, activeMapState);
    const entityLabel = seatsCount === 1 ? labels.singular : labels.plural;
    const selectedMapCount = (new Set(
        selectedSeats
            .map(seat => seat.seat_map_id)
            .filter(seatMapId => seatMapId)
    )).size;
    const mapContext = selectedMapCount > 1
        ? ` across ${selectedMapCount} maps`
        : '';

    return `${seatsCount} ${entityLabel} selected${mapContext}`;
}

function getClearSelectionCopy(selectedSeats, mapStates, activeMapState = null) {
    const labels = getEntityLabelsForSelectedSeats(selectedSeats, mapStates, activeMapState);
    const pluralLabel = capitalizeFirst(labels.plural);

    return {
        label: `Clear Selected ${pluralLabel}`,
        title: `Clear selected ${labels.plural}`,
    };
}

function getAlreadyTakenCopy(labels = DEFAULT_ENTITY_LABELS) {
    return `${capitalizeFirst(labels.singular)} already taken`;
}

function getCompanionPromptCopy(labels = DEFAULT_ENTITY_LABELS) {
    return {
        chosenText: `You've chosen ${getIndefiniteArticle(labels.accessible)} ${labels.accessible}.`,
        actionText: `Select companion ${labels.singular} also?`,
    };
}

export {
    DEFAULT_ENTITY_LABELS,
    getEntityLabelsFromDataset,
    getEntityLabelsForSelectedSeats,
    areDefaultSeatEntityLabels,
    getChooseEntityCopy,
    getReservedTicketTypeCardCopy,
    getSelectedSeatsIndicatorText,
    getClearSelectionCopy,
    getAlreadyTakenCopy,
    getCompanionPromptCopy,
};
